import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getCustomersPaginated } from '@/lib/api';
import { Search, Ruler, Users, ChevronRight, UploadCloud } from 'lucide-react';
import { formatCustomerNo } from '@/lib/displayUtils';

const PAGE_SIZE = 50;

const FittingStudioLandingPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    fetchCustomers(currentPage, debouncedSearchTerm);
  }, [currentPage, debouncedSearchTerm]);

  const fetchCustomers = async (page = 1, searchValue = '') => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: PAGE_SIZE,
      };
      if (searchValue) {
        params.search = searchValue;
      }

      const response = await getCustomersPaginated(params);
      const payload = response?.data || {};

      setCustomers(payload.data || []);
      setTotalPages(payload.total_pages || 0);
      setTotalCustomers(payload.total || 0);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const fromRow = totalCustomers > 0 ? ((currentPage - 1) * PAGE_SIZE) + 1 : 0;
  const toRow = totalCustomers > 0 ? Math.min(currentPage * PAGE_SIZE, totalCustomers) : 0;

  return (
    <div className="min-h-screen bg-[#f5f7fa] p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl font-semibold tracking-tight text-[#2d3748]">
              Fitting Studio
            </h1>
            <p className="text-muted-foreground">Select a customer to manage measurements and patterns</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or customer no..."
            value={searchTerm}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchTerm(e.target.value);
            }}
            className="pl-10 h-11 bg-white shadow-sm"
            data-testid="fitting-studio-client-search-input"
          />
        </div>

        <div className="flex items-center justify-between gap-3 text-sm" data-testid="fitting-studio-pagination-summary">
          <p className="text-muted-foreground">
            Showing {fromRow}–{toRow} of {totalCustomers} clients
          </p>
          <div className="flex items-center gap-2" data-testid="fitting-studio-pagination-controls">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              data-testid="fitting-studio-pagination-prev"
            >
              Previous
            </Button>
            <span className="text-muted-foreground" data-testid="fitting-studio-pagination-page-indicator">
              Page {totalPages === 0 ? 0 : currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={loading || totalPages === 0 || currentPage >= totalPages}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages || prev, prev + 1))}
              data-testid="fitting-studio-pagination-next"
            >
              Next
            </Button>
          </div>
        </div>

        {/* Customer List */}
        <div className="grid gap-4">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-xl shadow-sm border border-dashed">
              <Users className="w-12 h-12 text-muted-foreground/50 mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-1">No customers found</h3>
              <p className="text-muted-foreground text-sm">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer) => (
                <Card 
                  key={customer.customer_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer border-slate-200"
                  onClick={() => navigate(`/fitting-studio/${customer.customer_id}`)}
                  data-testid={`fitting-studio-client-card-${customer.customer_id}`}
                >
                  <CardContent className="p-5 flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{customer.customer_name}</h3>
                        <span className="text-xs font-mono text-muted-foreground bg-slate-100 px-1.5 py-0.5 rounded">
                          {formatCustomerNo(customer.customer_no)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {customer.email || 'No email'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/fitting-studio/${customer.customer_id}?tab=perth-import-wizard`);
                        }}
                        data-testid={`fitting-studio-client-perth-import-${customer.customer_id}`}
                      >
                        <UploadCloud className="w-3.5 h-3.5 mr-1" />
                        Perth Import
                      </Button>
                      <Button variant="ghost" size="icon" className="text-slate-400" data-testid={`fitting-studio-client-open-${customer.customer_id}`}>
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FittingStudioLandingPage;
